export type ServiceWorkerRecoveryResult = {
  unregisteredCount: number;
  deletedCacheCount: number;
  reloadTriggered: boolean;
};

type ServiceWorkerRegistrationLike = {
  scope?: string;
  unregister: () => Promise<boolean>;
};

type ServiceWorkerContainerLike = {
  getRegistrations: () => Promise<ServiceWorkerRegistrationLike[]>;
};

type CacheStorageLike = {
  keys: () => Promise<string[]>;
  delete: (key: string) => Promise<boolean>;
};

type LocationLike = {
  href: string;
  replace?: (url: string) => void;
  reload?: () => void;
};

type LoggerLike = Pick<Console, "info" | "warn">;

export type ResetRecipmonServiceWorkerAndCachesOptions = {
  serviceWorkerContainer?: ServiceWorkerContainerLike | null;
  cachesApi?: CacheStorageLike | null;
  locationRef?: LocationLike | null;
  origin?: string;
  cacheNamePrefixes?: string[];
  reloadParam?: string;
  skipReload?: boolean;
  reloadIfNoChanges?: boolean;
  logger?: LoggerLike;
};

const DEFAULT_CACHE_PREFIXES = ["recipmon-shell-", "recipmon-static-"];

export const resetRecipmonServiceWorkerAndCaches = async (
  options: ResetRecipmonServiceWorkerAndCachesOptions = {},
): Promise<ServiceWorkerRecoveryResult> => {
  const locationRef =
    options.locationRef ??
    (typeof window !== "undefined" ? (window.location as unknown as LocationLike) : null);
  const serviceWorkerContainer =
    options.serviceWorkerContainer ??
    (typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? (navigator.serviceWorker as unknown as ServiceWorkerContainerLike)
      : null);
  const cachesApi =
    options.cachesApi ??
    (typeof caches !== "undefined" ? (caches as unknown as CacheStorageLike) : null);
  const logger = options.logger ?? console;
  const origin =
    options.origin ??
    (() => {
      if (!locationRef?.href) return "";
      try {
        return new URL(locationRef.href).origin;
      } catch {
        return "";
      }
    })();
  const cacheNamePrefixes = options.cacheNamePrefixes ?? DEFAULT_CACHE_PREFIXES;
  const reloadParam = options.reloadParam ?? "sw_reset";

  let unregisteredCount = 0;
  let deletedCacheCount = 0;

  if (serviceWorkerContainer?.getRegistrations) {
    try {
      const registrations = await serviceWorkerContainer.getRegistrations();
      await Promise.all(
        registrations.map(async (registration) => {
          if (origin && registration.scope && !registration.scope.startsWith(origin)) {
            return;
          }
          const unregistered = await registration.unregister().catch(() => false);
          if (unregistered) {
            unregisteredCount += 1;
          }
        }),
      );
    } catch (error) {
      logger.warn("[startup-recovery] Failed to enumerate service worker registrations:", error);
    }
  }

  if (cachesApi?.keys) {
    try {
      const cacheNames = await cachesApi.keys();
      await Promise.all(
        cacheNames.map(async (cacheName) => {
          if (!cacheNamePrefixes.some((prefix) => cacheName.startsWith(prefix))) {
            return;
          }
          const deleted = await cachesApi.delete(cacheName).catch(() => false);
          if (deleted) {
            deletedCacheCount += 1;
          }
        }),
      );
    } catch (error) {
      logger.warn("[startup-recovery] Failed to clear app caches:", error);
    }
  }

  logger.info("[startup-recovery] Reset service worker/caches result", {
    unregisteredCount,
    deletedCacheCount,
  });

  const hasChanges = unregisteredCount > 0 || deletedCacheCount > 0;
  const shouldReload =
    !options.skipReload && locationRef && (hasChanges || options.reloadIfNoChanges === true);

  if (shouldReload) {
    try {
      const nextUrl = new URL(locationRef.href);
      if (nextUrl.searchParams.get(reloadParam) !== "1") {
        nextUrl.searchParams.set(reloadParam, "1");
      }
      if (typeof locationRef.replace === "function") {
        locationRef.replace(nextUrl.toString());
      } else if (typeof locationRef.reload === "function") {
        locationRef.reload();
      }
      return {
        unregisteredCount,
        deletedCacheCount,
        reloadTriggered: true,
      };
    } catch {
      if (typeof locationRef.reload === "function") {
        locationRef.reload();
        return {
          unregisteredCount,
          deletedCacheCount,
          reloadTriggered: true,
        };
      }
    }
  }

  return {
    unregisteredCount,
    deletedCacheCount,
    reloadTriggered: false,
  };
};
